import { useEffect, useRef, useState } from 'react';
import { amyLogo, hakusanaIcon } from '../brand/logos';
import SafeIcon from './SafeIcon';
import type { Language } from '../i18n/locales';
import { SUPPORTED_LOCALES } from '../i18n/locales';
import { useApp, timeLabel } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import { workspaces } from '../store/seed';
import type { Page } from '../types';

const searchablePages: { page: Page; label: string }[] = [
  { page: 'calendar', label: 'Calendar' },
  { page: 'ideas', label: 'Ideas & campaigns' },
  { page: 'materials', label: 'Material bank' },
  { page: 'brand', label: 'Brand' },
  { page: 'reports', label: 'Reports' },
  { page: 'allowance', label: 'Plan allowance' },
  { page: 'settings', label: 'Settings' },
];

function useCloseOnOutsideClickOrEscape(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const closeIfClickedOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) close();
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', closeIfClickedOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeIfClickedOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open, close]);
  return ref;
}

export default function Topbar({
  language,
  setLanguage,
  level,
  showWorkspace,
  onMenu,
  onReset,
}: {
  language: Language;
  setLanguage: (value: Language) => void;
  level: string;
  showWorkspace: boolean;
  onMenu: () => void;
  onReset: () => void;
}) {
  const { translate } = useApp();
  const { route, navigate, actor, role, openChat } = useUi();
  const { commands } = useDemo();
  const workspaceData = useWorkspaceData(route.workspaceId);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [noticesOpen, setNoticesOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const searchRef = useCloseOnOutsideClickOrEscape(searchOpen, () => setSearchOpen(false));
  const noticeRef = useCloseOnOutsideClickOrEscape(noticesOpen, () => setNoticesOpen(false));
  const profileRef = useCloseOnOutsideClickOrEscape(profileOpen, () => setProfileOpen(false));
  const unreadCount = workspaceData.notices.filter((item) => !item.read).length;

  const normalizedQuery = query.trim().toLowerCase();
  const matchesQuery = (text: string) => text.toLowerCase().includes(normalizedQuery);
  const results = normalizedQuery
    ? {
        posts: workspaceData.posts
          .filter((post) => matchesQuery(post.title) || matchesQuery(post.caption))
          .slice(0, 5),
        materials: workspaceData.assets.filter((asset) => matchesQuery(asset.name)).slice(0, 3),
        campaigns: workspaceData.campaigns
          .filter((campaign) => matchesQuery(campaign.name) || matchesQuery(campaign.brief))
          .slice(0, 3),
        conversations: workspaceData.conversations
          .filter(
            (conversation) =>
              matchesQuery(conversation.title) ||
              conversation.messages.some(
                (message) => message.showAsWritten && matchesQuery(message.text)
              )
          )
          .slice(0, 3),
        pages: searchablePages.filter((item) => matchesQuery(translate(item.label))),
      }
    : null;
  const resultCount = results
    ? results.posts.length +
      results.materials.length +
      results.campaigns.length +
      results.conversations.length +
      results.pages.length
    : 0;
  const dayLabel = (day: number) =>
    new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short' }).format(
      new Date(2026, 10, day)
    );

  const actorInitials = actor.name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2);
  const translatedRoleLabel = (() => {
    if (role === 'owner') return translate('Client owner');
    if (role === 'contributor') return translate('Client contributor');
    if (role === 'agency') return translate('Agency staff');
    return translate('Platform admin');
  })();

  function closePopoversAndNavigate(next: Parameters<typeof navigate>[0]) {
    setSearchOpen(false);
    setNoticesOpen(false);
    setQuery('');
    navigate(next);
  }

  function switchWorkspace(id: string) {
    const isPageTiedToWorkspace = route.page === 'post' || route.page === 'review';
    const pageToKeep: Page = isPageTiedToWorkspace ? 'calendar' : route.page;
    navigate({ workspaceId: id as never, page: pageToKeep });
  }

  return (
    <header className="topbar">
      <div className="brand-lockup">
        <img className="brand-logo" src={amyLogo} alt="AMY" width={114} height={32} />
      </div>
      <span className="top-divider" />
      <div className="agency-lockup">
        <img className="agency-logo" src={hakusanaIcon} alt="" width={28} height={28} />
        <strong>Hakusana</strong>
      </div>
      {showWorkspace && (
        <select
          className="workspace-select"
          value={route.workspaceId}
          onChange={(event) => switchWorkspace(event.target.value)}
          aria-label={translate('Workspace')}
          disabled={role === 'contributor'}
        >
          {workspaces
            .filter((workspace) => role !== 'contributor' || workspace.id === route.workspaceId)
            .map((workspace) => (
              <option key={workspace.id} value={workspace.id}>
                {workspace.name}
              </option>
            ))}
        </select>
      )}
      <span className="level-tag">{translate(level)}</span>

      {showWorkspace && (
        <div className="top-search top-popover-wrap" ref={searchRef}>
          <SafeIcon name="FiSearch" size={15} className="top-search-icon" />
          <input
            className="search-field"
            value={query}
            placeholder={translate('Search {ws}', { ws: workspaceData.workspace.name })}
            aria-label={translate('Search')}
            onChange={(event) => {
              setQuery(event.target.value);
              setSearchOpen(true);
            }}
            onFocus={() => query && setSearchOpen(true)}
          />
          {searchOpen && results && (
            <div className="top-popover search-results" role="listbox">
              {resultCount === 0 && (
                <p className="muted">
                  {translate('Nothing matches “{q}” in {ws}.', {
                    q: query,
                    ws: workspaceData.workspace.name,
                  })}
                </p>
              )}
              {results.posts.length > 0 && (
                <>
                  <h4>{translate('Posts')}</h4>
                  {results.posts.map((post) => (
                    <button
                      key={post.id}
                      className="popover-item"
                      onClick={() => closePopoversAndNavigate({ page: 'post', id: post.id })}
                    >
                      <img src={post.image} alt="" className="popover-thumb" />
                      <span>
                        <strong>{post.title}</strong>
                        <span className="muted">
                          {post.channel} · {dayLabel(post.day)} · {translate(post.status)}
                        </span>
                      </span>
                    </button>
                  ))}
                </>
              )}
              {results.materials.length > 0 && (
                <>
                  <h4>{translate('Materials')}</h4>
                  {results.materials.map((asset) => (
                    <button
                      key={asset.id}
                      className="popover-item"
                      onClick={() => closePopoversAndNavigate({ page: 'materials' })}
                    >
                      <SafeIcon name="FiImage" />
                      <span>
                        <strong>{asset.name}</strong>
                        <span className="muted">{translate(asset.kind)}</span>
                      </span>
                    </button>
                  ))}
                </>
              )}
              {results.campaigns.length > 0 && (
                <>
                  <h4>{translate('Campaigns')}</h4>
                  {results.campaigns.map((campaign) => (
                    <button
                      key={campaign.id}
                      className="popover-item"
                      onClick={() => closePopoversAndNavigate({ page: 'ideas', id: campaign.id })}
                    >
                      <SafeIcon name="FiZap" />
                      <span>
                        <strong>{campaign.name}</strong>
                        <span className="muted">{translate(campaign.status)}</span>
                      </span>
                    </button>
                  ))}
                </>
              )}
              {results.conversations.length > 0 && (
                <>
                  <h4>{translate('Conversations')}</h4>
                  {results.conversations.map((conversation) => (
                    <button
                      key={conversation.id}
                      className="popover-item"
                      onClick={() => {
                        setSearchOpen(false);
                        setQuery('');
                        openChat(undefined, conversation.id);
                      }}
                    >
                      <SafeIcon name="FiMessageCircle" />
                      <span>
                        <strong>
                          {conversation.showAsWritten
                            ? conversation.title
                            : translate(conversation.title)}
                        </strong>
                        <span className="muted">{translate('Open in the AMY chat')}</span>
                      </span>
                    </button>
                  ))}
                </>
              )}
              {results.pages.length > 0 && (
                <>
                  <h4>{translate('Pages')}</h4>
                  {results.pages.map((item) => (
                    <button
                      key={item.page}
                      className="popover-item"
                      onClick={() => closePopoversAndNavigate({ page: item.page })}
                    >
                      <SafeIcon name="FiArrowRight" />
                      <span>
                        <strong>{translate(item.label)}</strong>
                      </span>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className="top-actions">
        {showWorkspace && (
          <div className="top-popover-wrap" ref={noticeRef}>
            <button
              className="icon-button notice-button"
              aria-label={translate('Notifications')}
              aria-expanded={noticesOpen}
              onClick={() => setNoticesOpen(!noticesOpen)}
            >
              <SafeIcon name="FiBell" />
              {unreadCount > 0 && <span className="notice-dot">{unreadCount}</span>}
            </button>
            {noticesOpen && (
              <div className="top-popover notice-list">
                <div className="popover-head">
                  <h4>{translate('Notifications')}</h4>
                  {unreadCount > 0 && (
                    <button
                      className="button small ghost"
                      onClick={() => commands.markNoticesRead(route.workspaceId)}
                    >
                      {translate('Mark all as read')}
                    </button>
                  )}
                </div>
                {workspaceData.notices.length === 0 && (
                  <p className="muted">{translate('You are all caught up.')}</p>
                )}
                {[...workspaceData.notices].reverse().map((notice) => (
                  <button
                    key={notice.id}
                    className={`popover-item ${notice.read ? '' : 'unread'}`}
                    onClick={() => {
                      const [pageName, id] = notice.link.split('/');
                      closePopoversAndNavigate({ page: pageName as Page, id });
                    }}
                  >
                    <span className="notice-marker" />
                    <span>
                      <strong>{translate(notice.text, notice.variables)}</strong>
                      <span className="muted">{timeLabel(notice.at, language, translate)}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <select
          className="select-field language-choice"
          value={language}
          onChange={(event) => setLanguage(event.target.value as Language)}
          aria-label={translate('Language')}
        >
          {SUPPORTED_LOCALES.map((locale) => (
            <option key={locale.code} value={locale.code}>
              {locale.flag} · {locale.name}
            </option>
          ))}
        </select>
        <div className="top-popover-wrap" ref={profileRef}>
          <button
            className="top-control"
            aria-expanded={profileOpen}
            onClick={() => setProfileOpen(!profileOpen)}
          >
            {actorInitials} <SafeIcon name="FiChevronDown" size={14} />
          </button>
          {profileOpen && (
            <div className="top-popover profile-menu">
              <strong>{actor.name}</strong>
              <span className="muted">
                {translatedRoleLabel}
                {showWorkspace ? ` · ${workspaceData.workspace.name}` : ''}
              </span>
              <hr />
              <button
                className="popover-item"
                onClick={() => {
                  setProfileOpen(false);
                  onReset();
                }}
              >
                <SafeIcon name="FiRotateCcw" />
                <span>{translate('Reset demo data')}</span>
              </button>
              <div className="popover-item static muted">
                <SafeIcon name="FiLogOut" />
                <span>{translate('Sign out is not part of this demo')}</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <button
        className="icon-button mobile-menu-button"
        aria-label={translate('Open menu')}
        onClick={onMenu}
      >
        <SafeIcon name="FiMenu" />
      </button>
    </header>
  );
}
